import React, { useEffect, useMemo, useState } from "react";
import { Form, Col, Button, Container, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import Select from "react-select";
import DashboardHelper from "../../helpers/DashboardHelper";
import DataTable from "react-data-table-component";
import StorageHelper from "../../helpers/StorageHelper";
import {
  ADD_CROPS_TO_FIELDS,
  ERROR_FETCHING_FARM_BUSINESS,
  ERROR_FETCHING_FIELDS_WITH_NO_CROPS,
  FIELDS_NO_CROPS_SELECTED_YEAR,
  INCOMING_TOKEN_HAS_EXPIRED,
  SESSION_HAS_EXPIRED,
} from "../../configurations/MessageConstants";
import SessionHelper from "../../helpers/SessionHelper";
import UserService from "../../api-service/UserService";
import { debounce } from "lodash";
import CropService from "../../api-service/CropService";
import Alert from "../common/Alert";
import FieldFilters from "./FieldFilters";
import LoadingOverlay from "../common/LoadingOverlay";
import {
  DANGER,
  EMPTY,
  INFO,
  RESOURCES,
  SESSION_EXPIRED,
  SESSION_EXPIRED_MESSAGE,
} from "../../configurations/Constants";
import { StatusCodes } from "../../configurations/status-codes";

const customStyles = {
  header: {
    style: {
      textAlign: "left",
      paddingLeft: "5px",
      MinHeight: "50px",
    },
  },
  subHeader: {
    style: {
      textAlign: "left",
      paddingLeft: "5px",
    },
  },
  contextMenu: {
    style: {
      zIndex: "0",
    },
  },
};

const conditionalRowStyles = [
  {
    when: (row) => row.is_shared,
    style: {
      backgroundColor: "#fef9ff",
      color: "purple",
      fontWeight: "500",
    },
  },
];

function MultipleFieldCrops() {
  const CropsValue = useSelector((state) => state.field.crops);
  const TillagesValue = useSelector((state) => state.field.tillages);
  const cropArray = DashboardHelper.setCropTypeArray([], CropsValue);
  const yearArray = DashboardHelper.setYearDataArray([]);
  const [businessList, setBusinessList] = useState([]);
  const [farmList, setFarmList] = useState([]);
  const [initialFarmList, setInitialFarmList] = useState([]);
  const [selectedCrop, setSelectedCrop] = useState(cropArray[0]);
  const [selectedTillage, setSelectedTillage] = useState(TillagesValue[0]);
  const [selectedYear, setSelectedYear] = useState(yearArray[0]);
  const [selectedBusinessList, setSelectedBusinessList] = useState([]);
  const [selectedFarmList, setSelectedFarmList] = useState([]);
  const [searchValue, setSearchValue] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [column, setColumn] = useState(null);
  const [columnOrder, setColumnOrder] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState(EMPTY);
  const [variant, setVariant] = useState(EMPTY);
  const [overlayLoading, setOverlayLoading] = useState(false);

  const columns = [
    {
      id: 1,
      name: "Field",
      selector: (row) => row.field_name,
      sortable: true,
      reorder: true,
      value: "field_name",
    },
    {
      id: 2,
      name: "Acres",
      selector: (row) => row.acres,
      sortable: true,
      reorder: true,
      value: "acres",
    },

    {
      id: 3,
      name: "Business",
      selector: (row) => row.business_name,
      sortable: true,
      reorder: true,
      value: "business_name",
    },
    {
      id: 4,
      name: "Farm",
      selector: (row) => row.farm_name,
      sortable: true,
      reorder: true,
      value: "farm_name",
    },
  ];

  useEffect(() => {
    (async function () {
      try {
        setLoading(true);
        await Promise.all([
          getFarmsAndBusinesses(),
          fetchFieldsWithNoCrops(
            currentPage,
            perPage,
            selectedYear,
            selectedBusinessList,
            selectedFarmList,
            searchValue,
            column,
            columnOrder
          ),
        ]);
      } catch (error) {
        console.error(ERROR_FETCHING_FARM_BUSINESS, error);
        return false;
      }
    })();
  }, []);

  const getFarmsAndBusinesses = async () => {
    const payload = {
      keys: [RESOURCES],
    };
    try {
      const response = await UserService.getUserFieldDetails(payload);
      const farmsAndBusiness = response.data.data.resources;
      const businesses = farmsAndBusiness.businesses.map((business) => ({
        value: business,
        label: business,
      }));
      const farms = farmsAndBusiness.farms_and_businesses.map((item) => ({
        value: item.farm_name,
        label: item.farm_name,
        business: item.business_name,
      }));
      setBusinessList(businesses);
      setFarmList(farms);
      setInitialFarmList(farms);
    } catch (error) {
      console.error(ERROR_FETCHING_FARM_BUSINESS, error);
      const responseData = error.response.data;
      if (
        error.response.status === StatusCodes.UNAUTHORIZED &&
        responseData.message.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(SESSION_EXPIRED, true);
        StorageHelper.setItem(SESSION_EXPIRED_MESSAGE, SESSION_HAS_EXPIRED);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      }
    }
  };

  const fetchFieldsWithNoCrops = async (
    page,
    size,
    year,
    businesses,
    farms,
    searchText,
    column,
    columnOrder
  ) => {
    setClearSelectedRows(!clearSelectedRows);
    setLoading(true);
    let params = `?current_pg=${page}`;
    if (size) params = `${params}&rows_per_pg=${size}`;
    if (year !== null) params = `${params}&year=${year.value}`;

    if (businesses.length > 0) {
      let businessNameList = businesses.map((business) => business.value);
      params = `${params}&businesses=${businessNameList.join(",")}`;
    }

    if (farms.length > 0) {
      let farmNameList = farms.map((farm) => farm.value);
      params = `${params}&farms=${farmNameList.join(",")}`;
    }

    if (searchText) params = `${params}&search_text=${searchText}`;
    if (column !== null) params = `${params}&column=${column}`;
    if (columnOrder !== null) params = `${params}&column_order=${columnOrder}`;

    try {
      const response = await UserService.getUserFieldsWithNoCrops(params);
      let fieldData = response.data.data;
      if (fieldData) {
        setData(fieldData.data);
        setTotalRows(fieldData.totalRows);
        setLoading(false);
      }
    } catch (error) {
      console.error(ERROR_FETCHING_FIELDS_WITH_NO_CROPS, error);
    }
  };

  const HandleYearChange = async (year) => {
    fetchFieldsWithNoCrops(
      currentPage,
      perPage,
      year,
      selectedBusinessList,
      selectedFarmList,
      searchValue,
      column,
      columnOrder
    );
    setSelectedYear(year);
  };

  const HandleBusinessChange = async (businesses) => {
    let filteredFarmList = [];
    let businessNameList = [];
    setSelectedFarmList([]);
    if (!businesses || !businesses.length) {
      await fetchFieldsWithNoCrops(
        currentPage,
        perPage,
        selectedYear,
        [],
        [],
        searchValue,
        column,
        columnOrder
      );
      setSelectedBusinessList([]);
      setFarmList(initialFarmList);
      return;
    }
    setSelectedBusinessList(businesses);
    businesses.forEach((business) => {
      businessNameList.push(business.value);
      const list = initialFarmList.filter(
        (farm) => farm.business === business.value
      );
      filteredFarmList.push(...list);
    });
    await fetchFieldsWithNoCrops(
      currentPage,
      perPage,
      selectedYear,
      businesses,
      [],
      searchValue,
      column,
      columnOrder
    );
    setFarmList(filteredFarmList);
  };

  const HandleFarmChange = async (farms) => {
    if (!farms || !farms.length) {
      await fetchFieldsWithNoCrops(
        currentPage,
        perPage,
        selectedYear,
        selectedBusinessList,
        [],
        searchValue,
        column,
        columnOrder
      );
      setSelectedFarmList([]);
      return;
    }
    await fetchFieldsWithNoCrops(
      currentPage,
      perPage,
      selectedYear,
      selectedBusinessList,
      farms,
      searchValue,
      column,
      columnOrder
    );
    setSelectedFarmList(farms);
  };

  const handleSearchValue = async (e) => {
    await debouncedSearch(e.target.value);
  };

  const debouncedSearch = debounce(async (value) => {
    await fetchFieldsWithNoCrops(
      currentPage,
      perPage,
      selectedYear,
      selectedBusinessList,
      selectedFarmList,
      value,
      column,
      columnOrder
    );
    setSearchValue(value);
  }, 400);

  const handlePageChange = async (page) => {
    await fetchFieldsWithNoCrops(
      page,
      perPage,
      selectedYear,
      selectedBusinessList,
      selectedFarmList,
      searchValue,
      column,
      columnOrder
    );
    setCurrentPage(page);
  };
  const handlePerRowsChange = async (newPerPage, page) => {
    await fetchFieldsWithNoCrops(
      page,
      newPerPage,
      selectedYear,
      selectedBusinessList,
      selectedFarmList,
      searchValue,
      column,
      columnOrder
    );
    setPerPage(newPerPage);
  };

  const handleSort = async (column, sortDirection) => {
    await fetchFieldsWithNoCrops(
      currentPage,
      perPage,
      selectedYear,
      selectedBusinessList,
      selectedFarmList,
      searchValue,
      column.value,
      sortDirection
    );

    setColumn(column.value);
    setColumnOrder(sortDirection);
  };

  const HandleAddCrops = async () => {
    setOverlayLoading(true);
    const fieldIdArray = selectedFields.map((field) => field.field_id);
    const payload = {
      tillage: selectedTillage.value,
      crop: selectedCrop.value,
      year: selectedYear.value,
      fields: fieldIdArray,
    };
    setClearSelectedRows(!clearSelectedRows);
    try {
      await SessionHelper.handleRefreshToken();
      const response = await CropService.multiFieldsCropUpdate(payload);
      setVariant(INFO);
      setAlertMessage(response.data.message);
      window.scrollTo({ top: 0, behavior: "smooth" });
      await fetchFieldsWithNoCrops(
        currentPage,
        perPage,
        selectedYear,
        selectedBusinessList,
        selectedFarmList,
        searchValue,
        column,
        columnOrder
      );
      setOverlayLoading(false);
      setShowAlert(true);
    } catch (error) {
      if (error.response.status === StatusCodes.BAD_REQUEST) {
        let responseData = error.response.data;
        setVariant(DANGER);
        setAlertMessage(responseData.message);
        window.scrollTo({ top: 0, behavior: "smooth" });
        setOverlayLoading(false);
        setShowAlert(true);
      }
    }
  };

  const handleSelectedRowsChange = ({ selectedRows }) => {
    setSelectedFields(selectedRows);
  };

  const closeAlert = () => {
    setVariant(EMPTY);
    setAlertMessage(EMPTY);
    setShowAlert(false);
  };

  const contextActions = useMemo(() => {
    return (
      <Button
        type="button"
        onClick={() =>
          HandleAddCrops(selectedFields, selectedCrop, selectedTillage)
        }
      >
        Add Crops
      </Button>
    );
  }, [selectedFields, selectedCrop, selectedTillage]);

  return (
    <div className="multiple-field-crops">
      <LoadingOverlay
        showSpinner={true}
        active={overlayLoading}
        text="Adding Crops..."
      />
      <legend className="d-flex justify-content-end">
        <ul className="text-end p-0 m-0">
          <li className="shared shared-text">Shared Fields</li>
        </ul>
      </legend>
      {showAlert && (
        <Alert
          key="messageAlert"
          variant={variant}
          closeAlert={(e) => closeAlert(e)}
          alertMessage={alertMessage}
          dismissible
          className="demo"
        />
      )}
      <div style={{ backgroundColor: "#e9ecef" }}>
        <div className="container mb-1">
          <Row>
            <Form.Group as={Col} className="mt-3 mb-1">
              <h2>Crops</h2>
              <p className="text-muted">
                {FIELDS_NO_CROPS_SELECTED_YEAR}
                {ADD_CROPS_TO_FIELDS}
              </p>
            </Form.Group>
          </Row>
        </div>
      </div>

      <Container className="p-3 multiple-field-crops-body">
        <Row className="mb-4">
          <Form.Group as={Col} controlId="FormCropList" md={4}>
            <Form.Label className="label required-field">
              Select crop to add
            </Form.Label>
            <Select
              name="cropList"
              options={cropArray}
              value={selectedCrop}
              className="basic-multi-select"
              onChange={(event) => setSelectedCrop(event)}
            />
          </Form.Group>
          <Form.Group as={Col} controlId="FormTillageList" md={4}>
            <Form.Label className="label required-field">
              Select tillage to add
            </Form.Label>
            <Select
              name="tillageList"
              options={TillagesValue}
              value={selectedTillage}
              className="basic-multi-select"
              onChange={(event) => setSelectedTillage(event)}
            />
          </Form.Group>
          <Form.Group as={Col} controlId="FormYearList" md={4}>
            <Form.Label className="label required-field">
              Select year to add
            </Form.Label>
            <Select
              name="yearList"
              options={yearArray}
              value={selectedYear}
              className="basic-multi-select"
              onChange={HandleYearChange}
              placeholder={"Select Year"}
            />
          </Form.Group>
        </Row>
        <DataTable
          title={
            <div>
              <h3>Fields</h3>
              <h5>Filter and search for fields to add crops</h5>
            </div>
          }
          noDataComponent="There are no fields to display"
          customStyles={customStyles}
          conditionalRowStyles={conditionalRowStyles}
          columns={columns}
          data={data}
          progressPending={loading}
          pagination
          paginationServer
          onSort={handleSort}
          paginationRowsPerPageOptions={[10, 25, 50, 100, 150]}
          paginationTotalRows={totalRows}
          paginationDefaultPage={currentPage}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          selectableRows
          subHeader
          subHeaderComponent={
            <FieldFilters
              businessList={businessList}
              selectedBusinessList={selectedBusinessList}
              farmList={farmList}
              selectedFarmList={selectedFarmList}
              searchValue={searchValue}
              HandleBusinessChange={HandleBusinessChange}
              HandleFarmChange={HandleFarmChange}
              handleSearchValue={handleSearchValue}
            />
          }
          onSelectedRowsChange={handleSelectedRowsChange}
          contextActions={contextActions}
          clearSelectedRows={clearSelectedRows}
        />
      </Container>
    </div>
  );
}

export default MultipleFieldCrops;
